import type React from 'react';
import { useEffect, useRef } from 'react';
import type {
  BrandEmbassyChatHandoffStepComponent,
  DriftChatHandoffStepComponent,
  FormattedSalesforceExtraPrechatFormDetails,
  FreshchatChatHandoffStepComponent,
  GenericChatHandoffStepComponent,
  GorgiasChatHandoffStepComponent,
  TalkdeskChatHandoffStepComponent,
  HandoffComponentTypes,
  HandoffIntegrations,
  HandoffStepComponent,
  IntercomChatHandoffStepComponent,
  KustomerHandoffStepComponent,
  LiveChatChatHandoffStepComponent,
  SalesforceChatHandoffStepComponent,
  SalesforceMessagingHandoffStepComponent,
  SnapEngageChatHandoffStepComponent,
  UjetHandoffStepComponent,
  ZendeskHandoffStepComponent,
  ZendeskMessagingHandoffStepComponent,
  ZendeskTalkHandoffStepComponent,
  GladlyChatHandoffStepComponent,
} from 'api/types';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  selectConversationIdUsedInPreviousSession,
  selectFeatureFlags,
  updateConversationIdUsedInPreviousSession,
} from 'reducers/workflows';
import { useUpdateConversation } from 'components/App/hooks';
import { selectConversationId, selectHandoffStatus } from 'reducers/workflows';
import {
  setHandoffIntegration,
  setHandoffStatus,
} from 'actions/workflows/workflows';
import { successfulHandoffOnSubmit } from 'utils/successfulHandoffOnSubmit';
import { postHandoffMessage, postGenericHandoffMessage } from 'utils/handoff';
import { postMessage } from 'utils/postMessage';
import { selectIsCurrentTabPrimary } from 'persistence/persistenceBetweenTabs';
import { selectApiKey } from 'reducers/config';
import {
  ONE_HOUR,
  THIRTY_MINUTES,
  THREE_HOURS,
  setDisabledUntil,
} from 'utils/disableForethoughtWidget';
import { CHAT_HANDOFF_FAILURE_MESSAGE } from 'components/App/consts';
import { selectHandoffFailureMessage } from 'reducers/workflows/index';

interface WidgetHandoffProps {
  setStepSent: (query?: string) => void;
  widgetComponent: HandoffStepComponent;
}

const handoffComponentTypeToIntegration: Record<
  HandoffComponentTypes,
  HandoffIntegrations
> = {
  brand_embassy_chat_handoff: 'brand_embassy',
  drift_chat_handoff: 'drift',
  freshchat_chat_handoff: 'freshchat',
  generic_handoff: 'generic',
  gladly_chat_handoff: 'gladly',
  gorgias_chat_handoff: 'gorgias',
  intercom_chat_handoff: 'intercom',
  kustomer_chat_handoff: 'kustomer',
  live_chat_chat_handoff: 'live_chat',
  salesforce_chat_handoff: 'salesforce',
  salesforce_messaging_handoff: 'salesforce_messaging',
  snapengage_chat_handoff: 'snap_engage',
  talkdesk_chat_handoff: 'talkdesk',
  zendesk_chat_handoff: 'zendesk',
  zendesk_messaging_handoff: 'zendesk_messaging',
  zendesk_talk_handoff: 'zendesk_talk',
};

function componentFieldToString(fieldValue: string | null | undefined): string {
  return fieldValue || '';
}

function postHandoff({
  conversationID,
  conversationIdUsedInPreviousSession,
  featureFlags,
  integration,
  isCurrentTabPrimary,
  setStepSent,
  widgetComponent,
}: {
  conversationID: string;
  conversationIdUsedInPreviousSession?: string;
  featureFlags: string[];
  integration: HandoffIntegrations;
  isCurrentTabPrimary: boolean;
  setStepSent: (query?: string) => void;
  widgetComponent: HandoffStepComponent;
}) {
  switch (integration) {
    case 'zendesk': {
      const { component_fields: fields } =
        widgetComponent as ZendeskHandoffStepComponent;

      // In the case that department is stored as CV, department_with_id.id will be a CV id (in the format '{{uuid}}').
      // we need to use department_with_id.name instead in this case
      const shouldUseDepartmentWithId = Boolean(fields.department_with_id);
      const isDepartmentIdValid = !isNaN(
        parseInt(fields.department_with_id?.id || ''),
      );
      const department = shouldUseDepartmentWithId
        ? isDepartmentIdValid
          ? fields.department_with_id?.id
          : fields.department_with_id?.name
        : fields.department;

      postHandoffMessage({
        additionalParameters: {
          chatTags: [
            `ft-conversation-id_${conversationID}`,
            'ft-chat',
            ...(fields.chat_tags ?? []),
          ],
          conversationIdUsedInPreviousSession,
          hideZendeskWidgetDelay: fields.hide_zd_widget_delay,
          ignoreAgentAvailability: fields.ignore_agent_availability,
        },
        department: componentFieldToString(department),
        email: componentFieldToString(fields.visitor_email),
        featureFlags,
        forethoughtConversationID: conversationID,
        integration,
        isCurrentTabPrimary,
        name: componentFieldToString(fields.visitor_name),
        question: componentFieldToString(fields.visitor_question),
      });
      break;
    }
    case 'zendesk_messaging': {
      const { component_fields: fields } =
        widgetComponent as ZendeskMessagingHandoffStepComponent;
      const {
        auth_token: authToken,
        custom_fields_and_values,
        ticket_tags: ticketTags,
      } = fields;

      const customFieldsAndValues = custom_fields_and_values || {};

      const formattedCustomFieldsAndValues = Object.entries(
        customFieldsAndValues,
      ).map(([id, value]) => ({
        id,
        value,
      }));

      postHandoffMessage({
        additionalParameters: {
          authToken,
          customFieldsAndValues: formattedCustomFieldsAndValues,
          ticketTags,
        },
        forethoughtConversationID: conversationID,
        integration,
        isCurrentTabPrimary,
        question: componentFieldToString(fields.visitor_question),
      });
      break;
    }
    case 'zendesk_talk':
      const { component_fields: fields } =
        widgetComponent as ZendeskTalkHandoffStepComponent;
      const { jwt_token, line_id: lineId } = fields;

      postHandoffMessage({
        additionalParameters: {
          jwtToken: jwt_token,
          lineId,
          talkTicketTags: [
            `ft-conversation-id_${conversationID}`,
            'ft-talk-handoff',
          ],
        },
        forethoughtConversationID: conversationID,
        integration,
        isCurrentTabPrimary,
        question: componentFieldToString(fields.visitor_question),
      });

      break;
    case 'salesforce': {
      const { component_fields: fields } =
        widgetComponent as SalesforceChatHandoffStepComponent;
      const additionalParameters = fields.additional_parameters || {};
      const extraPrechatFormDetails = fields.extra_prechat_form_details || [];
      const formattedExtraPrechatFormDetails = extraPrechatFormDetails.map(
        (detail): FormattedSalesforceExtraPrechatFormDetails => ({
          displayToAgent: detail.display_to_agent,
          label: detail.label,
          transcriptFields: detail.transcript_fields,
          value: detail.value,
        }),
      );

      postHandoffMessage({
        additionalParameters: {
          ...additionalParameters,
          extraPrechatFormDetails: formattedExtraPrechatFormDetails,
        },
        email: componentFieldToString(fields.visitor_email),
        forethoughtConversationID: conversationID,
        integration,
        isCurrentTabPrimary,
        name: componentFieldToString(fields.visitor_name),
        question: componentFieldToString(fields.visitor_question),
      });
      break;
    }
    case 'brand_embassy': {
      const { component_fields: fields } =
        widgetComponent as BrandEmbassyChatHandoffStepComponent;
      postHandoffMessage({
        additionalParameters: {
          autoStartSession: fields.auto_start_session,
          customFields: fields.custom_fields,
        },
        forethoughtConversationID: conversationID,
        integration,
        isCurrentTabPrimary,
        name: componentFieldToString(fields.visitor_name),
        question: fields.visitor_question,
      });
      break;
    }
    case 'drift': {
      const { component_fields: fields } =
        widgetComponent as DriftChatHandoffStepComponent;
      postHandoffMessage({
        email: componentFieldToString(fields.visitor_email),
        forethoughtConversationID: conversationID,
        integration,
        isCurrentTabPrimary,
        name: componentFieldToString(fields.visitor_name),
        question: componentFieldToString(fields.visitor_question),
      });
      break;
    }
    case 'intercom': {
      const { component_fields: fields } =
        widgetComponent as IntercomChatHandoffStepComponent;
      postHandoffMessage({
        additionalParameters: {
          autoSendConfig: fields.auto_send_config,
          identityVerificationEnabled: fields.identity_verification_enabled,
          userid: fields.visitor_user_id,
        },
        email: componentFieldToString(fields.visitor_email),
        forethoughtConversationID: conversationID,
        integration,
        isCurrentTabPrimary,
        name: componentFieldToString(fields.visitor_name),
        question: componentFieldToString(fields.visitor_question),
      });
      break;
    }
    case 'kustomer': {
      const { component_fields: fields } =
        widgetComponent as KustomerHandoffStepComponent;
      postHandoffMessage({
        additionalParameters: {
          assistantId: fields.assistant_id,
          customFields: { ...fields.custom_fields },
          transcript: fields.transcript,
        },
        email: componentFieldToString(fields.visitor_email),
        featureFlags,
        forethoughtConversationID: conversationID,
        integration,
        isCurrentTabPrimary,
        name: componentFieldToString(fields.visitor_name),
        question: componentFieldToString(fields.visitor_question),
      });
      break;
    }
    case 'snap_engage': {
      const { component_fields: fields } =
        widgetComponent as SnapEngageChatHandoffStepComponent;
      const additionalParameters = fields.additional_parameters || {};
      postHandoffMessage({
        additionalParameters: { ...additionalParameters },
        email: componentFieldToString(fields?.visitor_email),
        forethoughtConversationID: conversationID,
        integration,
        isCurrentTabPrimary,
        name: componentFieldToString(fields.visitor_name),
        question: componentFieldToString(fields.visitor_question),
      });
      break;
    }
    case 'generic': {
      const { component_fields: fields } =
        widgetComponent as GenericChatHandoffStepComponent;
      postGenericHandoffMessage({
        eventData: { ...fields.event_data },
        integration,
        isCurrentTabPrimary,
        widgetOption: fields.widget_option,
      });
      setStepSent();
      break;
    }
    case 'ujet': {
      const { component_fields: fields } =
        widgetComponent as UjetHandoffStepComponent;
      postMessage({
        event: 'forethoughtWidgetHandoff',
        ticketId: fields.zendesk_ticket_id,
      });
      setStepSent();
      break;
    }
    case 'gorgias': {
      const { component_fields: fields } =
        widgetComponent as GorgiasChatHandoffStepComponent;
      postHandoffMessage({
        forethoughtConversationID: conversationID,
        integration,
        isCurrentTabPrimary,
        question: fields.question,
      });
      break;
    }
    case 'talkdesk': {
      const { component_fields: fields } =
        widgetComponent as TalkdeskChatHandoffStepComponent;
      postHandoffMessage({
        additionalParameters: {
          email: fields.email,
          name: fields.name,
          prechat_fields: fields.prechat_fields,
          transcript: fields.transcript,
          transcript_field: fields.transcript_field,
        },
        forethoughtConversationID: conversationID,
        integration,
        isCurrentTabPrimary,
        question: fields.question,
      });
      break;
    }
    case 'freshchat': {
      const { component_fields: fields } =
        widgetComponent as FreshchatChatHandoffStepComponent;
      postHandoffMessage({
        additionalParameters: {
          conversationProperties: fields.custom_conversation_attributes,
          firstName: componentFieldToString(fields.first_name),
          lastName: componentFieldToString(fields.last_name),
          tags: fields.tags,
        },
        email: componentFieldToString(fields.email),
        forethoughtConversationID: conversationID,
        integration,
        isCurrentTabPrimary,
        question: fields.question,
      });
      break;
    }
    case 'salesforce_messaging': {
      const { component_fields: fields } =
        widgetComponent as SalesforceMessagingHandoffStepComponent;
      postHandoffMessage({
        additionalParameters: {
          clearSessionBeforeHandoff: fields.clear_session_before_handoff,
          hiddenFields: fields.hidden_fields,
          visibleFields: fields.visible_fields,
        },
        forethoughtConversationID: conversationID,
        integration,
        isCurrentTabPrimary,
        question: fields.question,
      });
      break;
    }
    case 'live_chat': {
      const { component_fields: fields } =
        widgetComponent as LiveChatChatHandoffStepComponent;
      postHandoffMessage({
        email: componentFieldToString(fields.email),
        forethoughtConversationID: conversationID,
        integration,
        isCurrentTabPrimary,
        name: componentFieldToString(fields.name),
        question: fields.question,
      });
      break;
    }

    case 'gladly': {
      const { component_fields: fields } =
        widgetComponent as GladlyChatHandoffStepComponent;
      postHandoffMessage({
        email: fields.visitor_email,
        forethoughtConversationID: conversationID,
        integration,
        isCurrentTabPrimary,
        name: fields.visitor_name,
        question: fields.visitor_question,
      });
      break;
    }
  }
}

const Handoff: React.FC<React.PropsWithChildren<WidgetHandoffProps>> = ({
  setStepSent,
  widgetComponent,
}) => {
  const dispatch = useAppDispatch();
  const featureFlags = useAppSelector(selectFeatureFlags);
  const updateConversation = useUpdateConversation();
  const handoffStatus = useAppSelector(selectHandoffStatus);
  const handoffFailureMessage = useAppSelector(selectHandoffFailureMessage);
  const conversationID = useAppSelector(selectConversationId);
  const conversationIdUsedInPreviousSession = useAppSelector(
    selectConversationIdUsedInPreviousSession,
  );
  const isHandedOff = useRef(false);
  const isCurrentTabPrimary = useAppSelector(selectIsCurrentTabPrimary);
  const apiKey = useAppSelector(selectApiKey);

  const {
    component_fields: componentFields,
    component_type: componentType,
    isSent,
  } = widgetComponent;
  const integration = handoffComponentTypeToIntegration[componentType];

  useEffect(() => {
    if (isHandedOff.current) {
      return;
    }
    isHandedOff.current = true;
    if (!isSent && handoffStatus === 'not_started') {
      dispatch(setHandoffStatus('started'));
      dispatch(setHandoffIntegration(integration));
      postHandoff({
        conversationID,
        conversationIdUsedInPreviousSession,
        featureFlags,
        integration,
        isCurrentTabPrimary,
        setStepSent,
        widgetComponent,
      });

      if (integration === 'zendesk') {
        dispatch(updateConversationIdUsedInPreviousSession(conversationID));
      }
      dispatch(setHandoffStatus('complete'));
    }
  }, []);

  useEffect(() => {
    const handoffSuccessVariable = componentFields.handoff_successful_variable;

    // inline function to reduce code duplication
    function onSubmit(
      handoffStatus: boolean,
      handoffFailureMessage?: string | null,
    ) {
      if (handoffSuccessVariable) {
        const updatePayload: Record<string, string | boolean> = {
          [handoffSuccessVariable]: handoffStatus,
        };

        if (handoffFailureMessage) {
          updatePayload[CHAT_HANDOFF_FAILURE_MESSAGE] = handoffFailureMessage;
        }

        updateConversation(updatePayload);
      }
    }

    if (isSent) {
      return;
    }

    if (handoffStatus === 'failure') {
      onSubmit(false, handoffFailureMessage);
      dispatch(setHandoffStatus('not_started'));
      setStepSent();
    }

    if (handoffStatus === 'success') {
      successfulHandoffOnSubmit({
        dispatch,
        handoffSuccessVariable,
        updateConversation,
      });
      setStepSent();

      // disable forethought widget logic
      switch (integration) {
        case 'kustomer':
          if (featureFlags?.includes('kustomer_handoff_disable_forethought')) {
            setDisabledUntil({
              apiKey: apiKey,
              conversationId: conversationID,
              ms: THIRTY_MINUTES,
            });
          }
          break;
        case 'zendesk_messaging':
          if (
            featureFlags?.includes(
              'zendesk_messaging_handoff_disable_forethought',
            )
          ) {
            setDisabledUntil({
              apiKey: apiKey,
              conversationId: conversationID,
              ms: THREE_HOURS,
            });
          }
          break;
        case 'talkdesk':
          if (featureFlags?.includes('talkdesk_handoff_disable_forethought')) {
            setDisabledUntil({
              apiKey: apiKey,
              conversationId: conversationID,
              ms: THREE_HOURS,
            });
          }
          break;
        case 'intercom':
        case 'gladly':
          setDisabledUntil({
            apiKey: apiKey,
            conversationId: conversationID,
            ms: ONE_HOUR,
          });
          break;
        default:
          break;
      }
    }
  }, [handoffStatus, handoffFailureMessage]);

  return null;
};

export default Handoff;
